import React from "react"
import { Outlet } from "react-router-dom"
import Div100vh from "react-div-100vh"
import { Box, useTheme } from "@mui/material"
import { get } from "lodash"

import { useSponsoredFooterBanner } from "hooks/sponsored-content/useSponsoredFooterBanner"
import {
  ELECTRON_CONFIG_HEADER_IMAGE_KEY,
  ELECTRON_CONFIG_HEADER_IMAGE_OVERLAP_KEY,
  useElectronConfig,
} from "hooks/electron"

import { IndoorMapContainer } from "containers/IndoorMapContainer"
import { SearchContainer } from "containers/SearchContainer"

import { IAppLayoutProps } from "layouts/interface"
import { AdMediaFooterCarousel } from "components/domains/ads"
import { SearchPopupContent } from "components/domains/search/kiosk-v/SearchPopupContent"

import { Header, HeaderLogo } from "components/Header"
import { Clock } from "components/Clock"
import { Footer } from "components/Footer"
import { Image } from "components/Image"

const DEFAULT_SEARCH = <SearchContainer renderComponent={SearchPopupContent} />

export const AppLayout: React.FC<IAppLayoutProps> = ({
  search = DEFAULT_SEARCH,
  appNavigation = null,
}) => {
  const theme = useTheme()
  const styleOverrides = theme.components[`AppLayout`]?.styleOverrides || {}
  const { root, headerWrapper, contentWrapper, footerWrapper } = styleOverrides
  const { footerBanners, footerBannerConfig } = useSponsoredFooterBanner()
  const { config } = useElectronConfig()

  // This source is loaded from setting.json when working with the Electron app
  const headerImage = get(config, ELECTRON_CONFIG_HEADER_IMAGE_KEY, "")
  const headerImageOverlap = get(
    config,
    ELECTRON_CONFIG_HEADER_IMAGE_OVERLAP_KEY,
    false
  )

  return (
    <Div100vh data-name="AppLayout" style={root} className="AppLayout-root">
      <Box sx={headerWrapper} className="AppLayout-header">
        <Header
          logo={<HeaderLogo />}
          title={<Image src={headerImage} />}
          clock={<Clock />}
          className={headerImageOverlap ? "is-header-image-overlap" : ""}
        />
      </Box>
      <Box sx={contentWrapper} className="AppLayout-content">
        <IndoorMapContainer />
        <Outlet />
        {search || DEFAULT_SEARCH}
        {appNavigation && appNavigation}
      </Box>
      <Box sx={footerWrapper} className="AppLayout-footer">
        <>
          {footerBanners.length > 0 ? (
            <AdMediaFooterCarousel
              data={footerBanners}
              carouselOptions={footerBannerConfig}
            />
          ) : (
            <Footer />
          )}
        </>
      </Box>
    </Div100vh>
  )
}
